<template>
    <div class="flex flex-col gap-6 md:grid md:grid-cols-2 xl:grid-cols-3 w-full">
        <!-- Left column: Goals card -->
        <div class="min-w-0 flex flex-col gap-3 md:pr-6 md:border-r md:border-[#e2e8f0]">
            <div class="rounded-xl border border-valence-grey-250 bg-white px-4 py-3 flex flex-col gap-3">
                <div class="flex items-center gap-3">
                    <h2 class="scaffold-body text-valence-grey-900 truncate shrink-0">Team Goals</h2>
                </div>
                <section aria-label="Team suggestions" class="relative rounded-xl transition-colors">
                    <div class="flex flex-col gap-4 max-h-[640px] overflow-y-auto">
                        <!-- Group: tracked unprioritized -->
                        <div v-if="trackedSuggestions.length > 0" class="flex flex-col gap-2">
                            <h3 class="font-sans font-semibold text-[13px] uppercase tracking-wide text-valence-grey-600 flex items-center gap-1.5">
                                <span class="shrink-0 h-2 w-2 rounded-full bg-valence-grey-400"></span>
                                Unassigned Goals
                            </h3>
                            <TransitionGroup tag="div" name="goal-card" class="relative flex flex-col gap-3">
                                <GoalCard
                                    v-for="goal in trackedSuggestions"
                                    :key="goal.id"
                                    :goal="goal"
                                    :is-loading="goalIsLoading(goal.id)"
                                    :is-selected="goal.id === selectedGoalId"
                                    :is-suggestion="false"
                                    show-assign-button
                                    draggable="true"
                                    @dragstart="onDragStart(goal.id, 'unassigned_goals')"
                                    @dragend="onDragEnd"
                                    @select="emit('select', goal.id, 'unassigned_goals')"
                                    @assign="emit('assign', goal.id)" />
                            </TransitionGroup>
                        </div>

                        <!-- Group: Nadia's suggestions -->
                        <div class="flex flex-col gap-2">
                            <div class="flex items-center justify-between gap-2">
                                <h3 class="font-sans font-semibold text-[13px] uppercase tracking-wide text-valence-grey-600 flex items-center gap-1.5">
                                    <span class="shrink-0 h-2 w-2 rounded-full bg-valence-primary-400"></span>
                                    Nadia's suggestions
                                </h3>
                                <button type="button" class="details text-valence-primary-500 hover:underline" @click="emit('explain-suggestions')">How does Nadia suggest?</button>
                            </div>
                            <TransitionGroup v-if="extractedSuggestions.length > 0" tag="div" name="goal-card" class="relative flex flex-col gap-3">
                                <GoalCard
                                    v-for="goal in extractedSuggestions"
                                    :key="goal.id"
                                    :goal="goal"
                                    :is-loading="goalIsLoading(goal.id)"
                                    :is-selected="goal.id === selectedGoalId"
                                    :is-suggestion="true"
                                    show-assign-button
                                    draggable="true"
                                    @dragstart="onDragStart(goal.id, 'suggestions')"
                                    @dragend="onDragEnd"
                                    @select="emit('select', goal.id, 'suggestions')"
                                    @assign="emit('assign', goal.id)" />
                            </TransitionGroup>
                            <div v-else class="w-full px-6 py-8 flex items-center justify-center rounded-xl bg-valence-primary-100">
                                <p class="body-small text-valence-grey-600 text-center font-medium">Future suggestions will appear here</p>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>

        <!-- Horizontal divider on small screens -->
        <hr class="border-[#e2e8f0] md:hidden order-2 md:order-none" />

        <!-- Right column: per-connection journey cards -->
        <div class="order-first md:order-none xl:col-span-2 flex flex-col gap-3">
            <div
                v-if="lanes.length === 0"
                class="rounded-xl border border-dashed border-valence-grey-300 bg-transparent px-4 py-8 flex flex-col items-center justify-center gap-2 text-center">
                <i class="bi bi-people text-xl text-valence-grey-400" aria-hidden="true"></i>
                <span class="body-small text-valence-grey-600 font-medium">Team members will appear here once goals are assigned</span>
            </div>
            <div v-for="lane in lanes" :key="lane.connection.id" class="rounded-xl border border-valence-grey-250 bg-white px-4 py-3 flex flex-col transition-colors">
                <div class="flex items-center gap-3">
                    <BaseAvatar size="sm" theme="primary" :initials="getFirstLastInitials(lane.connection.name ?? '')" />
                    <div class="flex flex-col gap-0.5 min-w-0 flex-1">
                        <h3 class="scaffold-body-bold text-valence-grey-900 truncate">{{ lane.connection.name }}</h3>
                        <p v-if="lane.connection.job_title" class="details text-valence-grey-600 truncate">{{ lane.connection.job_title }}</p>
                    </div>
                    <span v-if="isLaneCollapsed(lane.connection.id)" class="details text-valence-grey-500 shrink-0">
                        {{ lane.journeyGoals.length }} {{ lane.journeyGoals.length === 1 ? "goal" : "goals" }} assigned
                    </span>
                    <button
                        type="button"
                        class="shrink-0 flex h-6 w-6 items-center justify-center rounded text-valence-grey-400 hover:bg-slate-100 hover:text-valence-grey-600"
                        :aria-expanded="!isLaneCollapsed(lane.connection.id)"
                        :aria-label="isLaneCollapsed(lane.connection.id) ? 'Expand goals' : 'Collapse goals'"
                        @click="toggleLaneCollapsed(lane.connection.id)">
                        <i class="bi text-xs" :class="isLaneCollapsed(lane.connection.id) ? 'bi-chevron-down' : 'bi-chevron-up'" aria-hidden="true"></i>
                    </button>
                </div>
                <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
                <div :class="['lane-body', { 'lane-body--collapsed': isLaneCollapsed(lane.connection.id) }]">
                    <div class="lane-body__inner">
                        <div class="pt-3">
                            <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
                            <section
                                :aria-label="`${lane.connection.name} priority goals drop zone`"
                                class="rounded-xl border-2 transition-colors min-h-[96px]"
                                :class="laneDropClasses(lane.connection.id)"
                                @dragover="canDropOnLane(lane.connection.id) ? $event.preventDefault() : undefined"
                                @dragenter="dragOverLaneId = lane.connection.id"
                                @dragleave="dragOverLaneId = dragOverLaneId === lane.connection.id ? null : dragOverLaneId"
                                @drop.prevent="handleDropOnLane(lane.connection.id)">
                                <TransitionGroup v-if="lane.journeyGoals.length > 0" tag="div" name="goal-card" class="relative grid grid-cols-1 xl:grid-cols-2 gap-2 items-start">
                                    <GoalCard
                                        v-for="goal in lane.journeyGoals"
                                        :key="goal.id"
                                        :goal="goal"
                                        :is-loading="goalIsLoading(goal.id)"
                                        :is-selected="goal.id === selectedGoalId"
                                        draggable="true"
                                        @dragstart="onDragStart(goal.id, 'team_lane', lane.connection.id)"
                                        @dragend="onDragEnd"
                                        @select="emit('select', goal.id, 'team_lane')"
                                        @discuss="emit('discuss', $event)" />
                                </TransitionGroup>
                            </section>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import { type Goal, type GoalConnection } from "~vue/types/goals";
import { isSuggestedGoal } from "~vue/utils/goals";
import { getFirstLastInitials } from "~vue/utils/stringUtils";
import { computed, onMounted, onUnmounted, ref } from "vue";

import GoalCard from "./GoalCard.vue";
import { type JourneysSection } from "./utils";

export interface TeamLane {
    connection: GoalConnection;
    journeyGoals: Goal[];
}

const props = withDefaults(
    defineProps<{
        suggestions: Goal[];
        lanes: TeamLane[];
        selectedGoalId?: string | null;
        goalIsLoading: (goalId: string) => boolean;
    }>(),
    {
        selectedGoalId: null,
    },
);

const emit = defineEmits<{
    select: [goalId: string, fromSection: JourneysSection];
    "drop-on-lane": [goalId: string, connectionId: string, fromSection: JourneysSection, fromConnectionId: string | null];
    "explain-suggestions": [];
    "drag-start": [goalId: string, fromSection: JourneysSection];
    "drag-end": [];
    assign: [goalId: string];
    discuss: [goalId: string];
}>();

const draggingGoalId = ref<string | null>(null);
const draggingFromZone = ref<JourneysSection | null>(null);
const draggingFromConnectionId = ref<string | null>(null);
const dragOverLaneId = ref<string | null>(null);

const mdQuery = window.matchMedia("(max-width: 767px)");
const collapsedLaneIds = ref<Set<string>>(mdQuery.matches ? new Set(props.lanes.map((l) => l.connection.id)) : new Set());
function isLaneCollapsed(connectionId: string): boolean {
    return collapsedLaneIds.value.has(connectionId);
}
function toggleLaneCollapsed(connectionId: string) {
    const next = new Set(collapsedLaneIds.value);
    if (next.has(connectionId)) {
        next.delete(connectionId);
    } else {
        next.add(connectionId);
    }
    collapsedLaneIds.value = next;
}
function expandLane(connectionId: string) {
    const next = new Set(collapsedLaneIds.value);
    next.delete(connectionId);
    collapsedLaneIds.value = next;
}

function handleBreakpointChange(e: MediaQueryListEvent) {
    if (e.matches) {
        collapsedLaneIds.value = new Set(props.lanes.map((l) => l.connection.id));
    }
}

onMounted(() => mdQuery.addEventListener("change", handleBreakpointChange));
onUnmounted(() => mdQuery.removeEventListener("change", handleBreakpointChange));

defineExpose({ expandLane });

function belongsInNadiaBucket(goal: Goal): boolean {
    return isSuggestedGoal(goal);
}

const extractedSuggestions = computed(() => props.suggestions.filter((g) => belongsInNadiaBucket(g)));
const trackedSuggestions = computed(() => props.suggestions.filter((g) => !belongsInNadiaBucket(g)));

function canDropOnLane(connectionId: string): boolean {
    if (!draggingGoalId.value) {
        return false;
    }
    if (draggingFromZone.value === "team_lane" && draggingFromConnectionId.value === connectionId) {
        return false;
    }
    return true;
}

function laneDropClasses(connectionId: string): string {
    if (!draggingGoalId.value) {
        return "border-transparent";
    }
    if (!canDropOnLane(connectionId)) {
        return "border-transparent opacity-50";
    }
    if (dragOverLaneId.value === connectionId) {
        return "border-valence-primary-400 bg-valence-primary-50";
    }
    return "border-valence-primary-200";
}

function onDragStart(goalId: string, fromZone: JourneysSection, fromConnectionId: string | null = null) {
    collapsedLaneIds.value = new Set();
    draggingGoalId.value = goalId;
    draggingFromZone.value = fromZone;
    draggingFromConnectionId.value = fromConnectionId;
    emit("drag-start", goalId, fromZone);
}

function onDragEnd() {
    draggingGoalId.value = null;
    draggingFromZone.value = null;
    draggingFromConnectionId.value = null;
    dragOverLaneId.value = null;
    emit("drag-end");
}

function handleDropOnLane(connectionId: string) {
    const goalId = draggingGoalId.value;
    const fromZone = draggingFromZone.value;
    const fromConnectionId = draggingFromConnectionId.value;
    onDragEnd();
    if (!goalId || !fromZone) {
        return;
    }
    const lane = props.lanes.find((l) => l.connection.id === connectionId);
    if (!lane) {
        return;
    }
    if (fromZone === "team_lane" && fromConnectionId === connectionId) {
        return;
    }
    expandLane(connectionId);
    emit("drop-on-lane", goalId, connectionId, fromZone, fromConnectionId);
}
</script>

<style scoped>
.goal-card-enter-active {
    transition: opacity 250ms ease;
}
.goal-card-enter-from {
    opacity: 0;
}
.goal-card-leave-active {
    transition: opacity 200ms ease;
    position: absolute;
    width: 0;
    overflow: hidden;
}
.goal-card-leave-to {
    opacity: 0;
}
.goal-card-move {
    transition: transform 300ms ease;
}

.lane-body {
    display: grid;
    grid-template-rows: 1fr;
    transition: grid-template-rows 300ms ease;
}
.lane-body--collapsed {
    grid-template-rows: 0fr;
    pointer-events: none;
}
.lane-body__inner {
    overflow: hidden;
}
</style>
